import ks from "./kBrowserShared";

/**
 * kWebPushClient - the BROWSER half of Web Push (the Node half that sends is kReusableCoreTS/kWebPush).
 *
 *   const Push = new kWebPushClient("/my-sw.js", MyPort);   // MyPort: where the public key comes from, where a
 *   Push.Register();                                       //         subscription is stored / forgotten
 *   Push.Enable(OnEnabled);                                // FROM A CLICK - the browser asks only from a gesture
 *
 * What it does: registers the service worker (which the HOST writes: it shows the push), asks the notification
 * permission, subscribes PushManager with the server's VAPID public key (base64url) and hands the subscription JSON to
 * the port; Disable unsubscribes and tells the port. State() says what a settings control should show.
 * Every outcome the owner can act on (blocked in the browser, no push service, the server refusing) comes back to the
 * caller as an outcome word + the browser's reason - the HOST words it; nothing here shows UI.
 *
 * Lifted 2026-11-01 from ProjectChats' PCHPushClient (its generic part) for WACommander, the second consumer.
 * ⚠ iPhone / iPad: Safari offers PushManager only to a site added to the Home Screen; Supported() is then false.
 */

/** The host's server: the VAPID public key, and storing / forgetting this device's subscription. */
export interface IkWebPushPort {
    PublicKey(Done: kWebPushKeyDone): void;
    Store(Sub: PushSubscriptionJSON, Done: kWebPushStepDone): void;
    Forget(Endpoint: string, Done: kWebPushStepDone): void;
}

/** Key = the base64url public key ("" = the server gave none; Why says why). */
export type kWebPushKeyDone = (Key: string, Why: string) => void;
/** Ok = the server did it; Why = its reason otherwise. */
export type kWebPushStepDone = (Ok: boolean, Why: string) => void;
/** Outcome: "on" | "off" | "denied" | "unsupported" | "failed"; Why = the browser's / server's reason ("" when none). */
export type kWebPushOutcome = (Outcome: string, Why: string) => void;

export class kWebPushClient {
    public static readonly On: string = "on";
    public static readonly Off: string = "off";
    public static readonly Denied: string = "denied";
    public static readonly Unsupported: string = "unsupported";
    public static readonly Failed: string = "failed";

    private Reg: ServiceWorkerRegistration | null = null;
    private RegWhy: string = "";

    constructor(private readonly WorkerUrl: string, private readonly Port: IkWebPushPort) { }

    /** true where this page can receive pushes: a secure context, service workers, PushManager, Notification. */
    public static Supported = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            const Secure: boolean = window.isSecureContext === true;
            return Secure && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushClient.Supported");
            return false;
        }
    };

    /** Registers the service worker once (call at start; harmless where unsupported). */
    public Register = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kWebPushClient.Supported() === false || this.Reg !== null) { return; }
            navigator.serviceWorker.register(this.WorkerUrl).then(this.OnRegistered, this.OnRegisterFailed);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushClient.Register");
        }
    };

    /** The registration, once the browser has it (null before, or when it refused). DOCUMENTED EXCEPTION: a field read. */
    public Registration = (): ServiceWorkerRegistration | null => this.Reg;

    /** What a settings control shows: on (granted + a subscription), off, denied, unsupported. */
    public State = (Done: kWebPushOutcome): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kWebPushClient.Supported() === false) { Done(kWebPushClient.Unsupported, ""); return; }
            if (Notification.permission === "denied") { Done(kWebPushClient.Denied, ""); return; }
            if (this.Reg === null || Notification.permission !== "granted") { Done(kWebPushClient.Off, this.RegWhy); return; }
            const Read: kWebPushStateRead = new kWebPushStateRead(Done);
            this.Reg.pushManager.getSubscription().then(Read.OnSub, Read.OnFail);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushClient.State");
        }
    };

    /** ⛔ FROM A CLICK: permission -> the server's key -> subscribe -> the port stores it. */
    public Enable = (Done: kWebPushOutcome): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kWebPushClient.Supported() === false) { Done(kWebPushClient.Unsupported, ""); return; }
            if (Notification.permission === "denied") { Done(kWebPushClient.Denied, ""); return; }
            if (this.Reg === null) { Done(kWebPushClient.Failed, this.RegWhy); return; }
            new kWebPushEnableRun(this.Reg, this.Port, Done).Begin();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushClient.Enable");
        }
    };

    /** Unsubscribes this device and tells the port; Done("off") when it is gone. */
    public Disable = (Done: kWebPushOutcome): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Reg === null) { Done(kWebPushClient.Off, ""); return; }
            new kWebPushDisableRun(this.Port, Done).Begin(this.Reg);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushClient.Disable");
        }
    };

    private OnRegistered = (Reg: ServiceWorkerRegistration): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Reg = Reg;
            this.RegWhy = "";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushClient.OnRegistered");
        }
    };

    // ERROR-SUPPRESSED-JUSTIFIED: a browser refusing the service worker (private mode, a policy) is a defined outcome -
    // Enable then answers "failed" with this reason; nothing else on the page depends on it.
    private OnRegisterFailed = (Err: unknown): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Reg = null;
            this.RegWhy = String(Err);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushClient.OnRegisterFailed");
        }
    };

    /** DOCUMENTED EXCEPTION: pure. base64url -> the bytes PushManager.subscribe wants as applicationServerKey. */
    public static KeyBytes = (Key: string): Uint8Array<ArrayBuffer> => {
        const B64: string = Key.replace(/-/g, "+").replace(/_/g, "/") + "===".substring(0, (4 - Key.length % 4) % 4);
        const Raw: string = atob(B64);
        const Out: Uint8Array<ArrayBuffer> = new Uint8Array(new ArrayBuffer(Raw.length));
        for (let I: number = 0; I < Raw.length; I++) { Out[I] = Raw.charCodeAt(I); }
        return Out;
    };
}

/** State(): the current subscription read. */
class kWebPushStateRead {
    constructor(private readonly Done: kWebPushOutcome) { }

    public OnSub = (Sub: PushSubscription | null): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Sub === null) { this.Done(kWebPushClient.Off, ""); return; }
            this.Done(kWebPushClient.On, "");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushStateRead.OnSub");
        }
    };

    // ERROR-SUPPRESSED-JUSTIFIED: the browser could not say (push service gone) - shown as off, with its reason.
    public OnFail = (Err: unknown): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Done(kWebPushClient.Off, String(Err));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushStateRead.OnFail");
        }
    };
}

/** One Enable: permission -> key -> subscribe -> store. Each step's refusal ends it with "denied" / "failed". */
class kWebPushEnableRun {
    constructor(private readonly Reg: ServiceWorkerRegistration, private readonly Port: IkWebPushPort,
        private readonly Done: kWebPushOutcome) { }

    public Begin = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            Notification.requestPermission().then(this.OnPermission, this.OnFailed);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushEnableRun.Begin");
        }
    };

    private OnPermission = (P: NotificationPermission): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (P === "denied") { this.Done(kWebPushClient.Denied, ""); return; }
            if (P !== "granted") { this.Done(kWebPushClient.Off, ""); return; }
            this.Port.PublicKey(this.OnKey);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushEnableRun.OnPermission");
        }
    };

    private OnKey = (Key: string, Why: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Key === "") { this.Done(kWebPushClient.Failed, Why); return; }
            const Opts: PushSubscriptionOptionsInit = { userVisibleOnly: true, applicationServerKey: kWebPushClient.KeyBytes(Key) };
            this.Reg.pushManager.subscribe(Opts).then(this.OnSubscribed, this.OnFailed);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushEnableRun.OnKey");
        }
    };

    private OnSubscribed = (Sub: PushSubscription): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Port.Store(Sub.toJSON(), this.OnStored);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushEnableRun.OnSubscribed");
        }
    };

    private OnStored = (Ok: boolean, Why: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Ok !== true) { this.Done(kWebPushClient.Failed, Why); return; }
            this.Done(kWebPushClient.On, "");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushEnableRun.OnStored");
        }
    };

    // ERROR-SUPPRESSED-JUSTIFIED: the browser refusing permission or a subscription (no push service, private mode) is a
    // defined outcome the host words for the owner, not a fault of the page.
    private OnFailed = (Err: unknown): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Done(kWebPushClient.Failed, String(Err));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushEnableRun.OnFailed");
        }
    };
}

/** One Disable: the subscription is read, unsubscribed, and the port forgets its endpoint. */
class kWebPushDisableRun {
    private Endpoint: string = "";

    constructor(private readonly Port: IkWebPushPort, private readonly Done: kWebPushOutcome) { }

    public Begin = (Reg: ServiceWorkerRegistration): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            Reg.pushManager.getSubscription().then(this.OnSub, this.OnFailed);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushDisableRun.Begin");
        }
    };

    private OnSub = (Sub: PushSubscription | null): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Sub === null) { this.Done(kWebPushClient.Off, ""); return; }
            this.Endpoint = Sub.endpoint;
            Sub.unsubscribe().then(this.OnGone, this.OnFailed);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushDisableRun.OnSub");
        }
    };

    private OnGone = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Port.Forget(this.Endpoint, this.OnForgotten);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushDisableRun.OnGone");
        }
    };

    private OnForgotten = (Ok: boolean, Why: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            // The device is unsubscribed whatever the server said: a stale endpoint is dropped by the sender (410).
            if (Ok !== true) { this.Done(kWebPushClient.Off, Why); return; }
            this.Done(kWebPushClient.Off, "");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushDisableRun.OnForgotten");
        }
    };

    // ERROR-SUPPRESSED-JUSTIFIED: the browser could not unsubscribe - a defined outcome, worded by the host.
    private OnFailed = (Err: unknown): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Done(kWebPushClient.Failed, String(Err));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kWebPushDisableRun.OnFailed");
        }
    };
}
